import { Cell } from '@table-library/react-table-library/table';
import { MetricDelta, MetricTrend, MetricValue } from './components/MetricCells';
import { deltaPercent } from './metrics';

/**
 * Rows whose request has not come back yet have no number to sort on. Both sort keys below fall
 * back to a sentinel lower than any real value (clicks and impressions never go negative, and a
 * change never drops past -100%), so a partially loaded table still sorts sensibly and the
 * pending rows stay grouped together instead of scattering through the results.
 */
const sortByValue = (name) => (array) =>
    array.sort((a, b) => (a.metrics?.[name]?.value ?? -1) - (b.metrics?.[name]?.value ?? -1));

const sortByDelta = (name) => (array) =>
    array.sort((a, b) => {
        const percent = (row) => deltaPercent(row.metrics?.[name]) ?? -Infinity;
        return percent(a) - percent(b);
    });

/**
 * Shades the three impressions cells so the eye can tell where one metric group ends and the next
 * begins. Colored in scss/metrics/DomainPerformance.scss; the header row is already that same grey
 * across its full width, so only the body cells need it.
 */
const IMPRESSIONS_CELL_CLASS = 'metric-impressions';

/**
 * Each metric occupies three columns — the period figure, its daily trend, and its change against
 * the prior period — so every value can be read, sorted and exported on its own. A metric given a
 * `cellClass` carries it across all three.
 */
const metricColumns = ({ t, label, name, format, cellClass }) => [
    {
        label,
        key: name,
        data: name,
        cellSize: 'minmax(110px, 1fr)',
        sortType: sortByValue(name),
        Component: Cell,
        className: cellClass,
        renderCell: ({ item, csvDownload }) => (
            <MetricValue
                metric={item.metrics?.[name]}
                status={item.status}
                error={item.error}
                format={format}
                csvDownload={csvDownload}
            />
        ),
    },
    {
        label: <span className="delta-sign">&#916;</span>,
        key: `${name}_delta`,
        data: `${name}_delta`,
        cellSize: 'minmax(100px, 1fr)',
        sortType: sortByDelta(name),
        Component: Cell,
        className: cellClass,
        renderCell: ({ item, csvDownload }) => (
            <MetricDelta
                metric={item.metrics?.[name]}
                status={item.status}
                error={item.error}
                csvDownload={csvDownload}
            />
        ),
    },
    {
        label: t('global.trend'),
        key: `${name}_trend`,
        data: `${name}_trend`,
        cellSize: 'minmax(160px, 2fr)',
        sortType: sortByValue(name),
        Component: Cell,
        className: cellClass,
        renderCell: ({ item, csvDownload }) => (
            <MetricTrend
                metric={item.metrics?.[name]}
                status={item.status}
                error={item.error}
                labels={item.metrics?.dates}
                keyName={label}
                format={format}
                csvDownload={csvDownload}
            />
        ),
    },
];

export const COLUMNS = (t) => [
    {
        label: t('global.domain'),
        key: 'siteUrl',
        data: 'siteUrl',
        sortType: 'string',
        cellSize: 'minmax(240px, 2fr)',
        Component: Cell,
        renderCell: ({ data, csvDownload }) => {
            if (csvDownload) return data;
            return <strong className="text-wrap">{data}</strong>;
        },
    },
    ...metricColumns({ t, label: t('global.clicks'), name: 'clicks', format: '0.0a' }),
    ...metricColumns({
        t,
        label: t('global.impressions'),
        name: 'impressions',
        format: '0.0a',
        cellClass: IMPRESSIONS_CELL_CLASS,
    }),
    ...metricColumns({ t, label: t('global.ctr'), name: 'ctr', format: '0.00%' }),
];
